import { useState, type FormEvent } from 'react';
import { Eye } from 'lucide-react';
import { Button } from '@/components/button';
import { Dialog } from '@/components/dialog';
import { EmailComposerDialog } from '@/components/email-composer';
import { Input } from '@/components/input';
import { useToast } from '@/components/toast';
import type { InvitationFields } from '@/lib/email-composer';
import { useCreateInvitation } from '@/lib/invitations';

/**
 * Invite someone in two steps: who they are, then the email they'll get.
 * Nothing is created until Send, so backing out of the review leaves no
 * half-made invitation behind.
 */
export function InviteDialog({
  open,
  onClose,
  title,
  role,
  clientId,
  hint,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  role: InvitationFields['role'];
  clientId?: number;
  hint: string;
}) {
  const create = useCreateInvitation();
  const toast = useToast();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [reviewing, setReviewing] = useState(false);

  if (!open) return null;

  const fields: InvitationFields = {
    name: name.trim(),
    email: email.trim(),
    role,
    client_id: clientId ?? null,
  };

  if (reviewing) {
    return (
      <EmailComposerDialog
        source={{ kind: 'invitation', fields }}
        title={title}
        confirmLabel="Send invitation"
        pending={create.isPending}
        // Back to the details, with what was typed still there.
        onClose={() => setReviewing(false)}
        lockedNote="The accept button, its private link, when it expires, and the signature are added below your message."
        onSend={async ({ subject, message }) => {
          await create.mutateAsync({ ...fields, subject, message });
          toast.success(`Invitation sent to ${fields.email}`);
          setName('');
          setEmail('');
          setReviewing(false);
          onClose();
        }}
      />
    );
  }

  function review(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setReviewing(true);
  }

  return (
    <Dialog open onClose={onClose} title={title}>
      <form className="space-y-3" onSubmit={review}>
        <Input required placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        <Input
          required
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <p className="text-xs text-[var(--color-foreground-muted)]">{hint}</p>
        <div className="flex justify-end">
          <Button type="submit">
            <Eye className="size-4" /> Review email
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
